// =============================================================================
// MeuDriver — Hook: usePwaInstall (Batch K3)
// Captura o evento beforeinstallprompt e expõe uma API para o banner de
// instalação do PWA.
//
// Uso:
//   const { canInstall, install, isInstalled } = usePwaInstall()
// =============================================================================

import { useState, useEffect, useCallback } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/**
 * Detecta se o PWA já está instalado checando o display mode.
 * Funciona em Chrome/Edge (standalone) e iOS (navigator.standalone).
 */
function detectInstalled(): boolean {
  if (typeof window === 'undefined') return false
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
  // iOS Safari expõe navigator.standalone
  const isIosStandalone = (navigator as { standalone?: boolean }).standalone === true
  return isStandalone || isIosStandalone
}

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled]       = useState(detectInstalled)

  useEffect(() => {
    // Já instalado: não precisa capturar o prompt
    if (isInstalled) return

    function handleBeforeInstall(e: Event) {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }

    function handleAppInstalled() {
      setIsInstalled(true)
      setDeferredPrompt(null)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstall)
    window.addEventListener('appinstalled',        handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall)
      window.removeEventListener('appinstalled',        handleAppInstalled)
    }
  }, [isInstalled])

  const install = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) return false
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    setDeferredPrompt(null)
    return outcome === 'accepted'
  }, [deferredPrompt])

  return {
    /** true se o browser disponibilizou o prompt de instalação */
    canInstall: deferredPrompt !== null,
    /** Dispara o prompt nativo de instalação. Retorna true se o usuário aceitou. */
    install,
    /** true se o app já está instalado (display: standalone ou iOS standalone) */
    isInstalled,
  }
}
